/**
 * The cap-and-input composite: the 3D cap seated ONTO the input's left edge
 * (negative margin + higher z) so they read as a single unit, with the input's
 * border/glow tracking the cap's "thinking" state so both light up together.
 *
 * CLONED verbatim from the Console (tc-lab src/shell/Composer.tsx). That
 * file's warning applies here too: this is the one piece of the assistant
 * nobody should ever see a different version of.
 */

import type { ReactNode } from 'react'
import { Component } from 'react'
import { Box, IconButton, Paper, Stack, TextField, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import ThinkingCap3D from './ThinkingCap3D'

/**
 * A missing/undeliverable cap model must NEVER take the shell down: the GLTF
 * loader throws (an asset 404 returns the SPA's index.html, which is not a
 * GLB — 2026-08-29), and an unhandled render error unmounts the whole app.
 * Fall back to the static cap mark; the composer keeps working.
 */
class CapBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  render() {
    if (this.state.failed) {
      return (
        <Box
          component="img" src="/brand/thinking-cap-logo.png" alt=""
          sx={{ width: 56, height: 56, objectFit: 'contain', ml: -1, mr: -1, mb: -0.5 }}
        />
      )
    }
    return this.props.children
  }
}

export default function Composer({
  value,
  onChange,
  onSend,
  onStop,
  sending,
  /** Bumped on each send so the cap bounces. */
  pulseKey,
  placeholder = 'Ask anything…',
  autoFocus,
  /** Voice input, or anything else that belongs left of Send. */
  extraActions,
  /** Buttons OUTSIDE the input, to its right. */
  trailingActions,
}: {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  /** Aborts the in-flight reply. While a turn is streaming, Stop takes the
   *  send button's seat — sending is impossible mid-turn anyway. */
  onStop?: () => void
  sending: boolean
  pulseKey: number
  placeholder?: string
  autoFocus?: boolean
  extraActions?: ReactNode
  trailingActions?: ReactNode
}) {
  return (
    <Box sx={{ px: 3, py: 2.5, flexShrink: 0 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto', position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            position: 'relative', zIndex: 2, ml: -3, mr: -3, mb: -0.5,
            filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.28))',
          }}
        >
          <CapBoundary>
            <ThinkingCap3D thinking={sending} pulseKey={pulseKey} size={166} />
          </CapBoundary>
        </Box>
        <Paper
          elevation={0}
          sx={(theme) => ({
            display: 'flex', alignItems: 'stretch', gap: 0.5,
            borderRadius: 7, flex: 1,
            border: '1.5px solid',
            borderColor: sending ? 'primary.main' : 'divider',
            boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
            transition: 'border-color 0.3s ease',
            animation: sending ? 'capGlow 1.4s ease-in-out infinite' : 'none',
            '@keyframes capGlow': {
              '0%,100%': { boxShadow: '0 10px 30px rgba(0,0,0,0.10)' },
              '50%': {
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}, 0 10px 30px rgba(0,0,0,0.14)`,
              },
            },
          })}
        >
          <TextField
            fullWidth placeholder={placeholder} value={value}
            variant="standard"
            autoFocus={autoFocus}
            InputProps={{ disableUnderline: true, sx: { height: '100%', pl: 5, pr: 1, py: 1.25, fontSize: '1.05rem' } }}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend() } }}
            multiline maxRows={4}
          />
          {extraActions}
          {sending && onStop ? (
            <Tooltip title="Stop the reply">
              <IconButton size="large" color="default" onClick={onStop} aria-label="stop the reply">
                <StopCircleOutlinedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton size="large" color="primary" onClick={onSend} disabled={!value.trim() || sending}>
              <SendIcon />
            </IconButton>
          )}
        </Paper>
        {trailingActions && (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, ml: 0.5 }}>
            {trailingActions}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
